/**
 * `/sign-in` view — issue #117.
 *
 * Two-step UX:
 *   1. Email + password. If the server returns `code: 'totp_required'`
 *      we reveal the TOTP input and re-submit the whole bundle on the
 *      second click. The server runs the password verification on both
 *      submits — there is no partial-token tied to the first step — so
 *      this is the cheapest correct contract.
 *   2. If the password was wrong, we surface `auth.sign_in.error_invalid`
 *      under the password field and move focus there.
 *
 * Form-level errors (rate limit, unverified email) render in the
 * `role="alert"` region above the submit row.
 *
 * On success the page reloads at the post-sign-in URL. Reloading flushes
 * the LEGACY_GM_SESSION fallback path and re-runs `bootSession()` with
 * the freshly-set cookie.
 */
import { button, card, textInput } from '@dl/ui';
import {
  div,
  form,
  h1,
  input,
  label,
  main,
  p,
  span,
  text,
  type Mountable,
  type Send,
  type Signal,
} from '@llui/dom';

import { t } from '../i18n.js';
import { appRouteLink, type AppRoute } from '../router.js';

import { signIn } from './api.js';
import { clearFormErrors, focusFirstError, setFieldError, setFormError, setSubmitting } from './forms.js';

interface SignInLocalState {
  totpRequired: boolean;
}

const localState: SignInLocalState = { totpRequired: false };

async function onSubmit(event: SubmitEvent): Promise<void> {
  event.preventDefault();
  const formEl = event.currentTarget as HTMLFormElement;
  clearFormErrors(formEl);
  const data = new FormData(formEl);
  const email = String(data.get('email') ?? '').trim();
  const password = String(data.get('password') ?? '');
  const totp = String(data.get('totp_code') ?? '').trim();

  let hasError = false;
  if (email.length === 0) {
    setFieldError(formEl, 'sign-in-email', t('auth.sign_in.email_label'));
    hasError = true;
  }
  if (password.length === 0) {
    setFieldError(formEl, 'sign-in-password', t('auth.sign_in.password_label'));
    hasError = true;
  }
  if (hasError) {
    focusFirstError(formEl);
    return;
  }

  setSubmitting(formEl, true);
  const args: { email: string; password: string; totp_code?: string } = { email, password };
  if (totp.length > 0) args.totp_code = totp;
  const result = await signIn(args);
  setSubmitting(formEl, false);

  if (result.ok) {
    // Navigate to the app root with a full load so the boot path picks up
    // the new cookie (assign replaces the old hash-wipe + reload).
    window.location.assign('/');
    return;
  }

  // Map server codes onto our messages.
  if (result.code === 'totp_required') {
    localState.totpRequired = true;
    const totpRow = formEl.querySelector<HTMLElement>('[data-totp-row]');
    if (totpRow) totpRow.removeAttribute('hidden');
    const totpInput = formEl.querySelector<HTMLInputElement>('#sign-in-totp');
    if (totpInput) totpInput.focus();
    setFormError(formEl, t('auth.sign_in.totp_help'));
    return;
  }
  if (result.code === 'email_not_verified') {
    setFormError(formEl, t('auth.sign_in.error_unverified'));
    return;
  }
  if (result.status === 429) {
    setFormError(formEl, t('auth.sign_in.error_locked'));
    return;
  }
  if (localState.totpRequired) {
    setFieldError(formEl, 'sign-in-totp', t('auth.sign_in.error_totp'));
    focusFirstError(formEl);
    return;
  }
  setFieldError(formEl, 'sign-in-password', t('auth.sign_in.error_invalid'));
  focusFirstError(formEl);
}

const handleSubmit = (event: Event): void => {
  void onSubmit(event as SubmitEvent);
};

export function viewSignIn<Model, Msg>(_state: Signal<Model>, send: Send<Msg>): Mountable {
  // Always start with TOTP row hidden — the local state may carry over
  // a previous attempt within the same mount, so honour it on render.
  const totpHidden = localState.totpRequired ? undefined : 'hidden';
  // Helper to optionally include the `hidden` attribute only when set.
  const totpRowAttrs: Record<string, string> = { 'data-totp-row': '', class: 'auth-form-row' };
  if (totpHidden) totpRowAttrs.hidden = '';
  return main({ role: 'main', class: 'auth-shell', 'data-testid': 'sign-in-view' }, [
    card({ variant: 'default', padding: 'xl', className: 'auth-card', children: [
      div({ class: 'auth-card-head' }, [
        h1([text(t('auth.sign_in.heading'))]),
        p({ class: 'auth-card-subtitle' }, [text(t('auth.sign_in.subtitle'))]),
      ]),
      form(
        {
          class: 'auth-form',
          'data-testid': 'sign-in-form',
          novalidate: true,
          onSubmit: handleSubmit,
        },
        [
          div({ class: 'auth-form-row' }, [
            label({ for: 'sign-in-email' }, [text(t('auth.sign_in.email_label'))]),
            textInput({
              type: 'email',
              testid: 'sign-in-email',
              attrs: {
                id: 'sign-in-email',
                name: 'email',
                autocomplete: 'username',
                required: true,
                'aria-describedby': 'sign-in-email-error',
              },
            }),
            span({ id: 'sign-in-email-error', 'data-error-for': 'sign-in-email', class: 'auth-field-error', role: 'alert' }, []),
          ]),
          div({ class: 'auth-form-row' }, [
            label({ for: 'sign-in-password' }, [text(t('auth.sign_in.password_label'))]),
            textInput({
              type: 'password',
              testid: 'sign-in-password',
              attrs: {
                id: 'sign-in-password',
                name: 'password',
                autocomplete: 'current-password',
                required: true,
                'aria-describedby': 'sign-in-password-error',
              },
            }),
            span({ id: 'sign-in-password-error', 'data-error-for': 'sign-in-password', class: 'auth-field-error', role: 'alert' }, []),
          ]),
          div(totpRowAttrs, [
            label({ for: 'sign-in-totp' }, [text(t('auth.sign_in.totp_label'))]),
            textInput({
              type: 'text',
              testid: 'sign-in-totp',
              attrs: {
                id: 'sign-in-totp',
                name: 'totp_code',
                inputmode: 'numeric',
                autocomplete: 'one-time-code',
                'aria-describedby': 'sign-in-totp-help sign-in-totp-error',
              },
            }),
            span({ id: 'sign-in-totp-help', class: 'auth-field-help' }, [text(t('auth.sign_in.totp_help'))]),
            span({ id: 'sign-in-totp-error', 'data-error-for': 'sign-in-totp', class: 'auth-field-error', role: 'alert' }, []),
          ]),
          div({ class: 'auth-form-row auth-form-row-inline' }, [
            label({ for: 'sign-in-remember', class: 'auth-checkbox-row' }, [
              input({
                id: 'sign-in-remember',
                name: 'remember',
                type: 'checkbox',
                'data-testid': 'sign-in-remember',
              }),
              span([text(t('auth.sign_in.remember_label'))]),
            ]),
          ]),
          p({ class: 'auth-form-error', 'data-form-error': '', role: 'alert' }, []),
          div({ class: 'auth-form-actions' }, [
            button({
              variant: 'primary',
              type: 'submit',
              testid: 'sign-in-submit',
              children: [text(t('auth.sign_in.submit'))],
            }),
          ]),
        ],
      ),
      div({ class: 'auth-form-links' }, [
        appRouteLink(
          send,
          { kind: 'forgot-password' } as AppRoute,
          { 'data-testid': 'sign-in-forgot-link' },
          [text(t('auth.sign_in.forgot_link'))],
          (route) => ({ type: 'navigate', route }) as unknown as Msg,
        ),
        span({ class: 'auth-link-sep', 'aria-hidden': 'true' }, ['·']),
        span([
          text(`${t('auth.sign_in.no_account')} `),
          appRouteLink(
            send,
            { kind: 'sign-up' } as AppRoute,
            { 'data-testid': 'sign-in-sign-up-link' },
            [text(t('auth.sign_in.sign_up_link'))],
            (route) => ({ type: 'navigate', route }) as unknown as Msg,
          ),
        ]),
      ]),
    ] }),
  ]);
}
